import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { useState } from 'react';
import { useDarkMode } from '../contexts/DarkModeContext';

// Minimal production-only client error reporter
const safeReportError = async (message, context = {}) => {
  try {
    if (process.env.NODE_ENV !== 'production') return;
    const payload = {
      message: String(message || 'unknown_error'),
      context: { location: typeof window !== 'undefined' ? (window.location?.pathname || '/') : '/', ...context },
      ts: Date.now(),
    };
    await fetch('/api/client-logs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch (_) {}
};

const WISE_EMAIL = 'ytproudltd@gmail.com';
const WISE_LINK = 'https://wise.com/pay/business/ytproudllc';
const QR_CODE_URL = 'https://ytproud.com/wp-content/uploads/elementor/thumbs/wise-quick-pay-qr-code-1-qxk8wjwde5a6gli08k64nkr98ke21zlqhafzjp4864.png';
// Use a modern Wise/finance illustration
const WISE_IMAGE_URL = 'https://assets-global.website-files.com/5f6b7190899c3e1a1c8b7b9e/63e3e2b2e2c7e2b2e2c7e2b2_wise-logo.svg';

const copyToClipboard = async (text) => {
  try {
    if (!navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      return true;
    }
    await navigator.clipboard.writeText(text);
    return true;
  } catch (_) {
    safeReportError('clipboard_copy_failed');
    return false;
  }
};

const downloadImage = (url, filename) => {
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return true;
  } catch (_) {
    safeReportError('download_image_failed');
    return false;
  }
};

// --- BankDetailsCards Component ---
const copyIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-[#00B9FF] hover:text-[#0099CC] cursor-pointer transition-colors duration-300">
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 6v-1.125A2.625 2.625 0 0 0 13.875 2.25h-7.5A2.625 2.625 0 0 0 3.75 4.875v10.5A2.625 2.625 0 0 0 6.375 18H7.5m3 0h7.125A2.625 2.625 0 0 0 20.25 15.375v-7.5A2.625 2.625 0 0 0 17.625 5.25H17.25M9 18a2.25 2.25 0 0 1-2.25-2.25V7.5A2.25 2.25 0 0 1 9 5.25h7.5A2.25 2.25 0 0 1 18.75 7.5v8.25A2.25 2.25 0 0 1 16.5 18H9Z" />
  </svg>
);

const usdDetails = {
  local: [
    { label: 'Wise Account Holder', value: 'YTproud LLC' },
    { label: 'Account holder', value: 'Wise US Inc.' },
    { label: 'ACH and Wire routing number', value: '084009519' },
    { label: 'Account number', value: '119835591292591' },
    { label: 'Account type', value: 'Checking' },
    { label: 'Bank name and address', value: 'Column National Association, A4-700 1 Letterman Drive, San Francisco CA 94129, United States' },
  ],
  global: [
    { label: 'Wise Account Holder', value: 'YTproud LLC' },
    { label: 'Account holder', value: 'Wise US Inc.' },
    { label: 'Swift/BIC', value: 'TRWIUS35XXX' },
    { label: 'Account number', value: '119835591292591' },
    { label: 'Bank name and address', value: 'Wise US Inc, 30 W. 26th Street, Sixth Floor, New York NY 10010, United States' },
  ],
  info: [
    "Local: There's a 6.11 USD fee to receive wire transfers. Payments take up to 3 working days to arrive.",
    "Global · Swift: Payments take up to 5 working days to arrive. There's a 6.11 USD fee to receive wire transfers."
  ]
};

const gbpDetails = {
  local: [
    { label: 'Account holder', value: 'YTproud LLC' },
    { label: 'Sort code', value: '23-08-01' },
    { label: 'Account number', value: '96652228' },
    { label: 'IBAN', value: 'GB97 TRWI 2308 0196 6522 28' },
    { label: 'Bank name and address', value: 'Wise Payments Limited, 56 Shoreditch High Street, London, E1 6JJ, United Kingdom' },
  ],
  global: [
    { label: 'Account holder', value: 'YTproud LLC' },
    { label: 'Swift/BIC', value: 'TRWIGB2LXXX' },
    { label: 'IBAN', value: 'GB97 TRWI 2308 0196 6522 28' },
    { label: 'Bank name and address', value: 'Wise Payments Limited, 56 Shoreditch High Street, London, E1 6JJ, United Kingdom' },
  ],
  info: [
    "Local: Payments take up to 1 working day to arrive.",
    "Global · Swift: Payments take up to 5 working days to arrive. Receive payments in 22 other currencies with these details. Fees apply."
  ]
};

function BankDetailsCards({ isDarkMode = true }) {
  const [usdTab, setUsdTab] = useState('local');
  const [gbpTab, setGbpTab] = useState('local');
  const [notification, setNotification] = useState('');

  const handleCopy = async (text) => {
    const ok = await copyToClipboard(text);
    if (ok) {
      setNotification('Copied to clipboard!');
    } else {
      setNotification('Copy failed');
    }
    setTimeout(() => setNotification(''), 2000);
  };

  const handleCopyAll = (region, tab) => {
    const details = (region === 'usd' ? usdDetails : gbpDetails)[tab]
      .map(row => `${row.label}: ${row.value}`)
      .join('\n');
    handleCopy(details);
  };

  return (
    <div className="flex flex-col md:flex-row gap-8 justify-center items-stretch">
      {/* USD Card */}
      <div className={`flex-1 min-w-[320px] max-w-xl rounded-2xl shadow-2xl p-6 md:p-8 flex flex-col transition-all duration-300 ${
        isDarkMode 
          ? 'bg-[#121212] shadow-2xl shadow-black/50' 
          : 'bg-white shadow-xl border border-gray-200'
      }`}>
        <div className={`flex justify-between items-center rounded-lg px-4 py-2 mb-4 transition-all duration-300 ${
          isDarkMode ? 'bg-[#1a1a1a]' : 'bg-gray-50'
        }`}>
          <div className={`flex items-center gap-2 text-lg font-bold transition-colors duration-300 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}><span role="img" aria-label="globe">🌐</span>Receive from a bank outside the US</div>
          <button className="bg-red-600 hover:bg-red-700 text-white font-semibold px-4 py-2 rounded-full text-xs transition-all duration-300 transform hover:scale-105 shadow-md focus:outline-none focus:ring-2 focus:ring-red-500/50" onClick={() => handleCopyAll('usd', usdTab)}>Copy All</button>
        </div>
        <div className={`flex gap-2 mb-4 rounded-lg p-1 transition-all duration-300 ${
          isDarkMode ? 'bg-[#1a1a1a]' : 'bg-gray-50'
        }`}>
          <button className={`flex-1 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
            usdTab === 'local' 
              ? 'bg-red-600 text-white shadow-md' 
              : isDarkMode ? 'text-gray-300' : 'text-gray-600'
          }`} onClick={() => setUsdTab('local')}>Local</button>
          <button className={`flex-1 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
            usdTab === 'global' 
              ? 'bg-red-600 text-white shadow-md' 
              : isDarkMode ? 'text-gray-300' : 'text-gray-600'
          }`} onClick={() => setUsdTab('global')}>Global · Swift</button>
        </div>
        <div className="flex-1">
          {(usdDetails[usdTab]).map((row, i) => (
            <div className="mb-4" key={row.label}>
              <div className={`font-bold text-base mb-1 transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>{row.label}</div>
              <div className={`flex items-center justify-between text-base bg-transparent transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-700'
              }`}>
                <span className="break-all">{row.value}</span>
                <span onClick={() => handleCopy(row.value)}>{copyIcon}</span>
              </div>
            </div>
          ))}
        </div>
        <div className={`rounded-lg p-4 mt-auto text-sm transition-all duration-300 ${
          isDarkMode ? 'bg-[#1a1a1a] text-white' : 'bg-gray-50 text-gray-700'
        }`}>
          <div>{usdDetails.info[0]}</div>
          <div className="mt-1">{usdDetails.info[1]}</div>
        </div>
      </div>
      {/* GBP Card */}
      <div className={`flex-1 min-w-[320px] max-w-xl rounded-2xl shadow-2xl p-6 md:p-8 flex flex-col transition-all duration-300 ${
        isDarkMode 
          ? 'bg-[#121212] shadow-2xl shadow-black/50' 
          : 'bg-white shadow-xl border border-gray-200'
      }`}>
        <div className={`flex justify-between items-center rounded-lg px-4 py-2 mb-4 transition-all duration-300 ${
          isDarkMode ? 'bg-[#1a1a1a]' : 'bg-gray-50'
        }`}>
          <div className={`flex items-center gap-2 text-lg font-bold transition-colors duration-300 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}><span role="img" aria-label="globe">🌐</span>Receive from a bank in the UK</div>
          <button className="bg-red-600 hover:bg-red-700 text-white font-semibold px-4 py-2 rounded-full text-xs transition-all duration-300 transform hover:scale-105 shadow-md focus:outline-none focus:ring-2 focus:ring-red-500/50" onClick={() => handleCopyAll('gbp', gbpTab)}>Copy All</button>
        </div>
        <div className={`flex gap-2 mb-4 rounded-lg p-1 transition-all duration-300 ${
          isDarkMode ? 'bg-[#1a1a1a]' : 'bg-gray-50'
        }`}>
          <button className={`flex-1 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
            gbpTab === 'local' 
              ? 'bg-red-600 text-white shadow-md' 
              : isDarkMode ? 'text-gray-300' : 'text-gray-600'
          }`} onClick={() => setGbpTab('local')}>Local</button>
          <button className={`flex-1 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
            gbpTab === 'global' 
              ? 'bg-red-600 text-white shadow-md' 
              : isDarkMode ? 'text-gray-300' : 'text-gray-600'
          }`} onClick={() => setGbpTab('global')}>Global · Swift</button>
        </div>
        <div className="flex-1">
          {(gbpDetails[gbpTab]).map((row, i) => (
            <div className="mb-4" key={row.label}>
              <div className={`font-bold text-base mb-1 transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>{row.label}</div>
              <div className={`flex items-center justify-between text-base bg-transparent transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-700'
              }`}>
                <span className="break-all">{row.value}</span>
                <span onClick={() => handleCopy(row.value)}>{copyIcon}</span>
              </div>
            </div>
          ))}
        </div>
        <div className={`rounded-lg p-4 mt-auto text-sm transition-all duration-300 ${
          isDarkMode ? 'bg-[#1a1a1a] text-white' : 'bg-gray-50 text-gray-700'
        }`}>
          <div>{gbpDetails.info[0]}</div>
          <div className="mt-1">{gbpDetails.info[1]}</div>
        </div>
      </div>
      {/* Notification */}
      {notification && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 bg-red-600 text-white px-6 py-3 rounded-lg shadow-lg z-50 text-base font-semibold animate-fade-in border border-red-700">
          {notification}
        </div>
      )}
    </div>
  );
}

const WisePaymentDetails = () => {
  const { isDarkMode } = useDarkMode();
  
  return (
    <div className={`min-h-screen flex flex-col transition-all duration-300 ${
      isDarkMode ? 'bg-[#0f0f0f]' : 'bg-gray-100'
    }`}>
      <SEOHead
        title="Wise & Bank Transfer Payment Details | YTProud"
        description="Complete your payment securely using Wise or direct bank transfer. Get USD and GBP account details, QR codes, and payment instructions for YTProud."
        keywords="wise payment, bank transfer, USD account, GBP account, payment details, secure payment, ytproud payment"
        canonical="https://ytproud.com/wise-payment-details"
        ogTitle="Wise & Bank Transfer Payment Details | YTProud"
        ogDescription="Complete your payment securely using Wise or direct bank transfer. Get USD and GBP account details, QR codes, and payment instructions."
        ogImage="/images/wise-payment.jpg"
        twitterTitle="Wise & Bank Transfer Payment Details | YTProud"
        twitterDescription="Complete your payment securely using Wise or direct bank transfer. Get USD and GBP account details, QR codes, and payment instructions."
        twitterImage="/images/wise-payment.jpg"
        schemaJsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Wise & Bank Transfer Payment Details",
          "description": "Complete your payment securely using Wise or direct bank transfer",
          "url": "https://ytproud.com/wise-payment-details",
          "mainEntity": {
            "@type": "Organization",
            "name": "YTProud",
            "url": "https://ytproud.com"
          }
        }}
      />
      <Header />
      <main className="flex-grow container mx-auto px-2 py-10">
        {/* Modern Wise Banner */}
        <div className={`relative overflow-hidden rounded-3xl mb-12 transition-all duration-300 ${
          isDarkMode 
            ? 'bg-gradient-to-br from-[#1a2332] via-[#2E4369] to-[#1a2332] shadow-2xl shadow-black/50' 
            : 'bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 shadow-xl'
        }`}>
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-32 h-32 bg-white rounded-full -translate-x-16 -translate-y-16"></div>
            <div className="absolute top-1/4 right-0 w-24 h-24 bg-white rounded-full translate-x-12"></div>
            <div className="absolute bottom-0 left-1/3 w-20 h-20 bg-white rounded-full -translate-y-10"></div>
          </div>
          
          <div className="relative p-8 md:p-12 flex flex-col md:flex-row items-center justify-between">
            <div className="flex-1 text-center md:text-left mb-8 md:mb-0">
              {/* Wise Logo Badge */}
              <div className="inline-flex items-center gap-3 mb-6 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
                <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L2 7L12 12L22 7L12 2Z" fill="#00B9FF"/>
                    <path d="M2 17L12 22L22 17" stroke="#00B9FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M2 12L12 17L22 12" stroke="#00B9FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <span className="text-white font-semibold text-sm">Wise Payment Gateway</span>
              </div>
              
              <h1 className="text-4xl md:text-6xl font-extrabold mb-6 text-white leading-tight">
                Wise &
                <span className={`block ${isDarkMode ? 'text-[#00D4AA]' : 'text-green-400'}`}>Bank Transfer</span>
              </h1>
              
              <p className="text-lg md:text-xl mb-6 text-white/90 max-w-2xl">
                Complete your payment securely using Wise's trusted global payment network. 
                Fast, transparent, and secure transfers worldwide.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <div className="flex items-center gap-2 text-white/80">
                  <div className={`w-2 h-2 rounded-full ${isDarkMode ? 'bg-[#00D4AA]' : 'bg-green-400'}`}></div>
                  <span className="text-sm">Instant setup</span>
                </div>
                <div className="flex items-center gap-2 text-white/80">
                  <div className={`w-2 h-2 rounded-full ${isDarkMode ? 'bg-[#00D4AA]' : 'bg-green-400'}`}></div>
                  <span className="text-sm">50+ currencies</span>
                </div>
                <div className="flex items-center gap-2 text-white/80">
                  <div className={`w-2 h-2 rounded-full ${isDarkMode ? 'bg-[#00D4AA]' : 'bg-green-400'}`}></div>
                  <span className="text-sm">Bank-level security</span>
                </div>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <p className="text-white font-semibold text-sm">
                  ⚠️ Important: Enter the full price of your purchase to avoid delays. 
                  Send payment confirmation via live chat after transfer.
            </p>
          </div>
            </div>
            
            <div className="flex-shrink-0 flex items-center justify-center">
              {/* Modern Wise-style icon */}
              <div className="relative">
                <div className="w-48 h-48 bg-white/10 backdrop-blur-sm rounded-3xl border border-white/20 flex items-center justify-center p-8">
                  <div className="relative">
                    {/* Globe base */}
                    <div className={`w-32 h-32 rounded-full flex items-center justify-center shadow-2xl ${
                      isDarkMode 
                        ? 'bg-gradient-to-br from-[#00B9FF] to-[#00D4AA]' 
                        : 'bg-gradient-to-br from-blue-500 to-green-500'
                    }`}>
                      {/* Currency symbols */}
                      <div className="absolute top-2 left-2 text-white font-bold text-lg">$</div>
                      <div className="absolute top-2 right-2 text-white font-bold text-lg">€</div>
                      <div className="absolute bottom-2 left-2 text-white font-bold text-lg">£</div>
                      <div className="absolute bottom-2 right-2 text-white font-bold text-lg">¥</div>
                      
                      {/* Center icon */}
                      <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2L2 7L12 12L22 7L12 2Z" fill="#00B9FF"/>
                          <path d="M2 17L12 22L22 17" stroke="#00B9FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M2 12L12 17L22 12" stroke="#00B9FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
                    </div>
                    
                    {/* Floating elements */}
                    <div className={`absolute -top-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center ${
                      isDarkMode ? 'bg-[#00D4AA]' : 'bg-green-500'
                    }`}>
                      <span className="text-white font-bold text-xs">✓</span>
                    </div>
                    <div className={`absolute -bottom-2 -left-2 w-6 h-6 rounded-full flex items-center justify-center ${
                      isDarkMode ? 'bg-[#00B9FF]' : 'bg-blue-500'
                    }`}>
                      <span className="text-white font-bold text-xs">⚡</span>
                    </div>
                  </div>
                </div>
                
                {/* Decorative rings */}
                <div className="absolute inset-0 border-2 border-white/20 rounded-3xl animate-pulse"></div>
                <div className="absolute inset-4 border border-white/10 rounded-2xl"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Modern Payment Methods Box */}
        <div className={`relative overflow-hidden rounded-2xl p-6 md:p-8 transition-all duration-300 ${
          isDarkMode 
            ? 'bg-gradient-to-br from-[#1a2332] via-[#2E4369] to-[#1a2332] shadow-2xl shadow-black/50' 
            : 'bg-gradient-to-br from-blue-50 via-blue-100 to-blue-50 shadow-xl border border-blue-200'
        }`}>
                      {/* Background Pattern */}
            <div className="absolute inset-0 opacity-5">
              <div className={`absolute top-6 left-6 w-12 h-12 rounded-full ${isDarkMode ? 'bg-red-600' : 'bg-blue-400'}`}></div>
              <div className={`absolute top-12 right-12 w-10 h-10 rounded-full ${isDarkMode ? 'bg-[#00D4AA]' : 'bg-green-400'}`}></div>
              <div className={`absolute bottom-12 left-1/4 w-8 h-8 rounded-full ${isDarkMode ? 'bg-[#2E4369]' : 'bg-blue-500'}`}></div>
            </div>

          {/* Header */}
          <div className="relative text-center mb-8">
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 backdrop-blur-sm rounded-full border mb-3 ${
              isDarkMode ? 'bg-white/10 border-white/20' : 'bg-blue-100 border-blue-200'
            }`}>
              <div className={`w-1.5 h-1.5 rounded-full animate-pulse ${isDarkMode ? 'bg-[#00D4AA]' : 'bg-green-500'}`}></div>
              <span className={`text-xs font-medium transition-colors duration-300 ${
                isDarkMode ? 'text-white/90' : 'text-gray-700'
              }`}>Choose Your Payment Method</span>
            </div>
            <h2 className={`text-2xl md:text-3xl font-bold mb-2 transition-colors duration-300 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>Wise Details</h2>
            <p className={`text-base transition-colors duration-300 ${
              isDarkMode ? 'text-white/80' : 'text-gray-600'
            }`}>Select your preferred payment method below</p>
          </div>

                      {/* Payment Cards Grid */}
            <div className="relative grid grid-cols-1 md:grid-cols-3 gap-6">
              
              {/* QR Code Card */}
              <div className={`group relative overflow-hidden rounded-xl p-4 transition-all duration-500 transform hover:scale-105 ${
                isDarkMode 
                  ? 'bg-gradient-to-br from-[#232b43] to-[#1a1f2e] border border-white/10' 
                  : 'bg-gradient-to-br from-white to-blue-50 border border-blue-200'
              }`}>
              {/* Card Header */}
              <div className="flex items-center gap-2 mb-4">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  isDarkMode 
                    ? 'bg-gradient-to-br from-[#2E4369] to-[#1a2a4a]' 
                    : 'bg-gradient-to-br from-blue-500 to-blue-600'
                }`}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="3" width="18" height="18" rx="2" stroke="white" strokeWidth="2"/>
                    <path d="M9 9h6v6H9z" fill="white"/>
                    <circle cx="12" cy="12" r="2" fill="white"/>
                  </svg>
                </div>
                <div>
                  <h3 className={`font-bold text-base transition-colors duration-300 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>QR Code</h3>
                  <p className={`text-xs transition-colors duration-300 ${
                    isDarkMode ? 'text-white/60' : 'text-gray-500'
                  }`}>Scan & Pay</p>
          </div>
        </div>

              {/* QR Code Display */}
              <div className={`relative mb-4 rounded-lg p-3 transition-all duration-300 ${
                isDarkMode ? 'bg-black/20' : 'bg-gray-100'
              }`}>
                <img src={QR_CODE_URL} alt="Wise QR Code" className="w-full h-24 object-contain" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent rounded-lg"></div>
            </div>

              {/* Action Button */}
            <button
                className={`w-full text-white font-semibold py-2.5 px-4 rounded-lg focus:outline-none focus:ring-2 transition-all duration-300 transform hover:scale-105 shadow-md group-hover:shadow-lg text-sm ${
                  isDarkMode 
                    ? 'bg-gradient-to-r from-[#2E4369] to-[#1a2a4a] hover:from-[#1a2a4a] hover:to-[#0f1a2a] focus:ring-[#2E4369]/50' 
                    : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 focus:ring-blue-500/50'
                }`}
              onClick={() => downloadImage(QR_CODE_URL, 'wise-qr-code.png')}
            >
                <div className="flex items-center justify-center gap-2">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="7,10 12,15 17,10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="12" y1="15" x2="12" y2="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Download QR Code
                </div>
            </button>
          </div>

            {/* Email Card */}
            <div className={`group relative overflow-hidden rounded-xl p-4 transition-all duration-500 transform hover:scale-105 ${
              isDarkMode 
                ? 'bg-gradient-to-br from-[#232b43] to-[#1a1f2e] border border-white/10' 
                : 'bg-gradient-to-br from-white to-blue-50 border border-blue-200'
            }`}>
              {/* Card Header */}
              <div className="flex items-center gap-2 mb-4">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  isDarkMode 
                    ? 'bg-gradient-to-br from-[#2E4369] to-[#1a2a4a]' 
                    : 'bg-gradient-to-br from-green-500 to-green-600'
                }`}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="white" strokeWidth="2"/>
                    <polyline points="22,6 12,13 2,6" stroke="white" strokeWidth="2"/>
                  </svg>
                </div>
                <div>
                  <h3 className={`font-bold text-base transition-colors duration-300 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>Email Transfer</h3>
                  <p className={`text-xs transition-colors duration-300 ${
                    isDarkMode ? 'text-white/60' : 'text-gray-500'
                  }`}>Send via Email</p>
                </div>
              </div>

              {/* Email Display */}
              <div className={`mb-4 p-3 rounded-lg transition-all duration-300 ${
                isDarkMode ? 'bg-black/20 border border-white/10' : 'bg-gray-100 border border-gray-200'
              }`}>
                <div className="flex items-center gap-2">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                    isDarkMode ? 'bg-[#00D4AA]' : 'bg-green-500'
                  }`}>
                    <span className="text-white font-bold text-xs">@</span>
                  </div>
                  <div className="flex-1">
                    <p className={`text-xs font-medium transition-colors duration-300 ${
                      isDarkMode ? 'text-white/80' : 'text-gray-600'
                    }`}>Wise Email</p>
                    <p className={`font-mono text-xs transition-colors duration-300 ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>{WISE_EMAIL}</p>
                  </div>
                </div>
              </div>

              {/* Action Button */}
              <button
                className={`w-full text-white font-semibold py-2.5 px-4 rounded-lg focus:outline-none focus:ring-2 transition-all duration-300 transform hover:scale-105 shadow-md group-hover:shadow-lg text-sm ${
                  isDarkMode 
                    ? 'bg-gradient-to-r from-[#2E4369] to-[#1a2a4a] hover:from-[#1a2a4a] hover:to-[#0f1a2a] focus:ring-[#2E4369]/50' 
                    : 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 focus:ring-green-500/50'
                }`}
                onClick={() => copyToClipboard(WISE_EMAIL)}
              >
                <div className="flex items-center justify-center gap-2">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  Copy Email Address
                </div>
              </button>
            </div>

                        {/* Link Card */}
            <div className={`group relative overflow-hidden rounded-xl p-4 transition-all duration-500 transform hover:scale-105 ${
              isDarkMode 
                ? 'bg-gradient-to-br from-[#232b43] to-[#1a1f2e] border border-white/10' 
                : 'bg-gradient-to-br from-white to-blue-50 border border-blue-200'
            }`}>
              {/* Card Header */}
              <div className="flex items-center gap-2 mb-4">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  isDarkMode 
                    ? 'bg-gradient-to-br from-[#2E4369] to-[#1a2a4a]' 
                    : 'bg-gradient-to-br from-blue-600 to-blue-700'
                }`}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" stroke="white" strokeWidth="2"/>
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" stroke="white" strokeWidth="2"/>
                  </svg>
                </div>
                <div>
                  <h3 className={`font-bold text-base transition-colors duration-300 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>Direct Link</h3>
                  <p className={`text-xs transition-colors duration-300 ${
                    isDarkMode ? 'text-white/60' : 'text-gray-500'
                  }`}>Visit Wise</p>
                </div>
              </div>

              {/* Link Display */}
              <div className={`mb-4 p-3 rounded-lg transition-all duration-300 ${
                isDarkMode ? 'bg-black/20 border border-white/10' : 'bg-gray-100 border border-gray-200'
              }`}>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-[#2E4369] rounded-full flex items-center justify-center">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" stroke="white" strokeWidth="2"/>
                      <polyline points="15,3 21,3 21,9" stroke="white" strokeWidth="2"/>
                      <line x1="10" y1="14" x2="21" y2="3" stroke="white" strokeWidth="2"/>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className={`text-xs font-medium transition-colors duration-300 ${
                      isDarkMode ? 'text-white/80' : 'text-gray-600'
                    }`}>Wise Payment Link</p>
                    <p className={`font-mono text-xs transition-colors duration-300 ${
                      isDarkMode ? 'text-white/70' : 'text-gray-500'
                    }`}>wise.com/pay/business/...</p>
                  </div>
            </div>
          </div>

              {/* Action Buttons */}
              <div className="space-y-2">
                <button
                  className={`w-full text-white font-semibold py-2 px-4 rounded-lg focus:outline-none focus:ring-2 transition-all duration-300 transform hover:scale-105 shadow-md text-sm ${
                    isDarkMode 
                      ? 'bg-gradient-to-r from-[#2E4369] to-[#1a2a4a] hover:from-[#1a2a4a] hover:to-[#0f1a2a] focus:ring-[#2E4369]/50' 
                      : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:ring-blue-600/50'
                  }`}
                  onClick={() => copyToClipboard(WISE_LINK)}
                >
                  <div className="flex items-center justify-center gap-2">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  Copy Link
                  </div>
                </button>
                <a
                  href={WISE_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`block w-full text-white font-semibold py-2 px-4 rounded-lg focus:outline-none focus:ring-2 transition-all duration-300 transform hover:scale-105 shadow-md text-center text-sm ${
                    isDarkMode 
                      ? 'bg-gradient-to-r from-[#2E4369] to-[#1a2a4a] hover:from-[#1a2a4a] hover:to-[#0f1a2a] focus:ring-[#2E4369]/50' 
                      : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 focus:ring-blue-500/50'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" stroke="currentColor" strokeWidth="2"/>
                      <polyline points="15,3 21,3 21,9" stroke="currentColor" strokeWidth="2"/>
                      <line x1="10" y1="14" x2="21" y2="3" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    Open Wise
                  </div>
                </a>
              </div>
            </div>
          </div>

          {/* Bottom Info */}
          <div className="relative mt-8 text-center">
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-300 ${
              isDarkMode ? 'bg-white/10 border border-white/20' : 'bg-gray-100 border border-gray-200'
            }`}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span className={`text-xs font-medium transition-colors duration-300 ${
                isDarkMode ? 'text-white/80' : 'text-gray-600'
              }`}>All payment methods are secure and encrypted</span>
            </div>
          </div>
        </div>
      </main>
      {/* Bank Details Section */}
      <section className="container mx-auto px-2 pb-12">
        <h2 className={`text-2xl md:text-3xl font-extrabold text-center mb-8 mt-2 transition-colors duration-300 ${
          isDarkMode ? 'text-white' : 'text-gray-900'
        }`}>USD Account Details & GBP Account Details</h2>
        <BankDetailsCards isDarkMode={isDarkMode} />
      </section>
      <Footer />
    </div>
  );
};

export default WisePaymentDetails; 